import { useEffect, useState } from "react";
import {
  BadgeCheck,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FilePenLine,
  Flame,
  History,
  Image,
  ShieldX,
  Trash2,
  UserCheck,
  X,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import Loader from "@/components/ui/loader";

interface AuditLog {
  id: string;
  listing_title: string | null;
  actor_name: string;
  actor_role: string;
  action: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  notes: string | null;
  created_at: string;
}

const ACTION_META: Record<
  string,
  { label: string; icon: React.ReactNode; color: string; bg: string }
> = {
  status_changed: {
    label: "Status Changed",
    icon: <CheckCircle2 className="h-4 w-4" />,
    color: "text-emerald-700",
    bg: "bg-emerald-100",
  },
  approved: {
    label: "Listing Approved",
    icon: <BadgeCheck className="h-4 w-4" />,
    color: "text-emerald-700",
    bg: "bg-emerald-100",
  },
  rejected: {
    label: "Listing Rejected",
    icon: <ShieldX className="h-4 w-4" />,
    color: "text-rose-700",
    bg: "bg-rose-100",
  },
  field_edited: {
    label: "Fields Edited",
    icon: <FilePenLine className="h-4 w-4" />,
    color: "text-blue-700",
    bg: "bg-blue-100",
  },
  created: {
    label: "Listing Created",
    icon: <Flame className="h-4 w-4" />,
    color: "text-amber-700",
    bg: "bg-amber-100",
  },
  deleted: {
    label: "Listing Deleted",
    icon: <Trash2 className="h-4 w-4" />,
    color: "text-rose-700",
    bg: "bg-rose-100",
  },
  agent_assigned: {
    label: "BL Advisor Assigned",
    icon: <UserCheck className="h-4 w-4" />,
    color: "text-purple-700",
    bg: "bg-purple-100",
  },
  image_updated: {
    label: "Image Updated",
    icon: <Image className="h-4 w-4" />,
    color: "text-slate-700",
    bg: "bg-slate-100",
  },
  notes_updated: {
    label: "Notes Updated",
    icon: <FilePenLine className="h-4 w-4" />,
    color: "text-indigo-700",
    bg: "bg-indigo-100",
  },
  advisor_checklist: {
    label: "Pre-Publish Checklist",
    icon: <ClipboardCheck className="h-4 w-4" />,
    color: "text-teal-700",
    bg: "bg-teal-100",
  },
};

const CHECKLIST_ITEMS: { key: string; label: string }[] = [
  { key: "owner_connected", label: "Connected with owner" },
  { key: "due_diligence_completed", label: "Due diligence completed" },
  { key: "nda_signed", label: "NDA signed with owner" },
];

const ROLE_BADGE: Record<string, string> = {
  admin: "bg-rose-100 text-rose-700",
  bl_advisor: "bg-purple-100 text-purple-700",
  seller: "bg-amber-100 text-amber-700",
  buyer: "bg-sky-100 text-sky-700",
};

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

function DiffRow({ label, old, newVal }: { label: string; old: unknown; newVal: unknown }) {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 py-1 text-xs">
      <span className="truncate rounded bg-rose-50 px-1.5 py-0.5 font-mono text-rose-700 line-through">
        {formatValue(old)}
      </span>
      <span className="text-muted-foreground">→</span>
      <span className="truncate rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-emerald-700">
        {formatValue(newVal)}
      </span>
      <span className="col-span-3 text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

function AuditCard({ log }: { log: AuditLog }) {
  const meta = ACTION_META[log.action] ?? {
    label: log.action,
    icon: <Clock className="h-4 w-4" />,
    color: "text-slate-700",
    bg: "bg-slate-100",
  };

  const date = new Date(log.created_at);
  const dateStr = date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  const timeStr = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  const oldFields = log.old_value ? Object.keys(log.old_value) : [];
  const newFields = log.new_value ? Object.keys(log.new_value) : [];
  const allFields = [...new Set([...oldFields, ...newFields])];

  return (
    <div className="relative pl-8">
      {/* Timeline dot */}
      <div className={`absolute left-0 top-1 flex h-7 w-7 items-center justify-center rounded-full ${meta.bg} ${meta.color}`}>
        {meta.icon}
      </div>
      {/* Card */}
      <div className="mb-6 rounded-xl border border-border/70 bg-white p-4 shadow-sm">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <span className={`text-sm font-bold ${meta.color}`}>{meta.label}</span>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-foreground">{log.actor_name}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.1em] ${
                  ROLE_BADGE[log.actor_role] ?? "bg-slate-100 text-slate-700"
                }`}
              >
                {log.actor_role.replace("_", " ")}
              </span>
            </div>
          </div>
          <div className="text-right text-[0.68rem] text-muted-foreground">
            <div className="font-semibold">{dateStr}</div>
            <div>{timeStr}</div>
          </div>
        </div>

        {/* Status change arrow */}
        {log.action === "status_changed" && log.old_value && log.new_value && (
          <div className="mt-3 flex items-center gap-2">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
              {String(log.old_value.status_label ?? log.old_value.status_id ?? "—")}
            </span>
            <span className="text-muted-foreground">→</span>
            <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
              {String(log.new_value.status_label ?? log.new_value.status_id ?? "—")}
            </span>
          </div>
        )}

        {/* Field diffs */}
        {log.action === "field_edited" && allFields.length > 0 && (
          <div className="mt-3 divide-y divide-border/50 rounded-lg border border-border/60 bg-muted/30 px-3 py-1">
            {allFields.map((f) => (
              <DiffRow
                key={f}
                label={f.replace(/_/g, " ")}
                old={log.old_value?.[f]}
                newVal={log.new_value?.[f]}
              />
            ))}
          </div>
        )}

        {/* Agent assigned */}
        {log.action === "agent_assigned" && log.new_value && (
          <div className="mt-3 rounded-lg bg-purple-50 px-3 py-2 text-xs text-purple-800">
            BL Advisor assigned:{" "}
            <span className="font-bold">
              {log.new_value.agent_name
                ? String(log.new_value.agent_name)
                : log.new_value.agent_id
                ? String(log.new_value.agent_id)
                : "—"}
            </span>
          </div>
        )}

        {/* Pre-publish due-diligence checklist */}
        {log.action === "advisor_checklist" && log.new_value && (
          <div className="mt-3 space-y-1.5 rounded-lg border border-teal-100 bg-teal-50/60 px-3 py-2.5">
            {CHECKLIST_ITEMS.map((item) => {
              const ok = log.new_value?.[item.key] === true;
              return (
                <div key={item.key} className="flex items-center gap-2 text-xs">
                  {ok ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  ) : (
                    <XCircle className="h-4 w-4 shrink-0 text-rose-500" />
                  )}
                  <span className={ok ? "text-foreground" : "text-muted-foreground line-through"}>
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Notes */}
        {log.notes && (
          <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="font-bold">Note:</span> {log.notes}
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  listingId: string;
  listingTitle: string;
  open: boolean;
  onClose: () => void;
}

// ── Unique actors summary ─────────────────────────────────────────────────────
function ActorsSummary({ logs }: { logs: AuditLog[] }) {
  // Deduplicate by actor_name + actor_role, preserving first-seen order
  const seen = new Set<string>();
  const actors: { name: string; role: string }[] = [];
  for (const log of [...logs].reverse()) {
    const key = `${log.actor_name}::${log.actor_role}`;
    if (!seen.has(key)) {
      seen.add(key);
      actors.push({ name: log.actor_name, role: log.actor_role });
    }
  }
  if (actors.length === 0) return null;

  return (
    <div className="mb-5 rounded-xl border border-border/70 bg-card p-4">
      <p className="mb-3 text-[0.68rem] font-bold uppercase tracking-widest text-muted-foreground">
        Participants ({actors.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {actors.map((a) => {
          const initials = a.name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();
          const badgeCls = ROLE_BADGE[a.role] ?? "bg-slate-100 text-slate-700";
          // Pick avatar bg by role
          const avatarBg: Record<string, string> = {
            admin: "bg-rose-100 text-rose-700",
            bl_advisor: "bg-purple-100 text-purple-700",
            seller: "bg-amber-100 text-amber-700",
            buyer: "bg-sky-100 text-sky-700",
          };
          const avCls = avatarBg[a.role] ?? "bg-slate-100 text-slate-600";
          return (
            <div
              key={`${a.name}::${a.role}`}
              className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-2.5 py-1.5"
            >
              <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[0.6rem] font-bold ${avCls}`}>
                {initials}
              </div>
              <span className="text-xs font-semibold text-foreground">{a.name}</span>
              <span className={`rounded-full px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-wider ${badgeCls}`}>
                {a.role.replace("_", " ")}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ListingAuditDrawer({ listingId, listingTitle, open, onClose }: Props) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get<AuditLog[]>(`/admin/listings/${listingId}/audit-logs`)
      .then(setLogs)
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [open, listingId]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-background shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border/80 bg-card px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <History className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-foreground">Audit Trail</h2>
              <p className="line-clamp-1 text-xs text-muted-foreground">{listingTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <Loader className="min-h-[30vh]" />
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-muted/60">
                <History className="h-8 w-8 text-muted-foreground/40" />
              </div>
              <p className="mt-4 font-semibold text-foreground">No audit history yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Actions by admins and BL advisors will appear here.
              </p>
            </div>
          ) : (
            <>
              <ActorsSummary logs={logs} />
              <div className="relative border-l-2 border-border/60 pl-2">
                {logs.map((log) => (
                  <AuditCard key={log.id} log={log} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border/80 bg-card px-6 py-4">
          <p className="text-xs text-muted-foreground">
            {logs.length} event{logs.length !== 1 ? "s" : ""} recorded for this listing
          </p>
        </div>
      </div>
    </>
  );
}
